// Libraries
import { useNavigate } from 'react-router-dom'
// MUI
import { Box } from '@mui/material'
// Types
import { KmsVehicle, Services } from '../../../../../store/slices/timesheet/timesheetPageSlice'
// Hooks
import { useServiceCalculations } from './hooks/useServiceCalculations'
import { useServiceHandlers } from './hooks/useServiceHandlers'
import { useServiceState } from './hooks/useServiceState'
// Components
import ServiceActions from './ServiceActions'
import ServiceExecutionDateTime from './ServiceExecutionDateTime'
import ServiceKmsDetails from './ServiceKmsDetails'
import ServicePassengers from './ServicePassengers'
import ServiceTimeDetails from './ServiceTimeDetails'

interface ServiceSummaryProps {
	serviceDetail: Services
	instance: number
	kmsVehicles: KmsVehicle[]
}

export default function ServiceSummary({ serviceDetail, instance, kmsVehicles }: Readonly<ServiceSummaryProps>) {
	const state = useServiceState(serviceDetail, instance)
	const navigate = useNavigate()
	const calculations = useServiceCalculations(serviceDetail, state, kmsVehicles)
	const handlers = useServiceHandlers(instance, state)
	const isEditDisabled = calculations.isTowDriversService && calculations.isSecondaryDriverService

	function onPrintService(e: React.MouseEvent<Element, MouseEvent>) {
		e.stopPropagation()
		navigate(`./print-service/${serviceDetail.service.serviceId}`)
	}

	return (
		<Box
			sx={{
				position: 'relative',
				left: 0,
				margin: 0,
				padding: 0,
				width: '100%',
				display: 'grid',
				gridTemplateColumns: 'minmax(320px, 640px) 14.25rem 10.25rem 80px 69px 84px 75px',
			}}
		>
			<ServiceTimeDetails
				isReserveService={calculations.isReserveService}
				isTowDriversService={calculations.isTowDriversService}
				isContinuousService={calculations.isContinuousService}
				isSecondaryDriverService={calculations.isSecondaryDriverService}
				serviceDetail={serviceDetail}
				currentServiceDay={calculations.currentServiceDay}
				totalServiceDays={calculations.totalServiceDays}
				continuedServiceStartDate={calculations.continuedServiceStartDate}
				continuedServiceEndDate={calculations.continuedServiceEndDate}
			/>
			<ServiceExecutionDateTime
				isReserveService={calculations.isReserveService}
				isSecondaryDriverService={isEditDisabled}
				startTime={state.startTime}
				endTime={state.endTime}
				startDate={state.startDate}
				endDate={state.endDate}
				onStartTimeChange={handlers.handleStartHoursProcessed}
				onEndTimeChange={handlers.handleEndHoursProcessed}
				onStartDateChange={handlers.handleStartDateProcessed}
				onEndDateChange={handlers.handleEndDateProcessed}
				endTimeTooltipOpen={state.endTimeTooltipOpen}
				setEndTimeTooltipOpen={state.setEndTimeTooltipOpen}
				startTimeTooltipOpen={state.startTimeTooltipOpen}
				serviceDetail={serviceDetail}
				setStartTimeTooltipOpen={state.setStartTimeTooltipOpen}
				showEndTimeTooltip={calculations.showEndTimeTooltip}
				startTimeMatches={calculations.startTimeMatches}
			/>
			<ServiceKmsDetails
				isReserveService={calculations.isReserveService}
				isSecondaryDriverService={calculations.isSecondaryDriverService}
				calculations={calculations}
				fleetNumber={serviceDetail.service.vehicle.fleetNumber}
				tooltipState={{
					totalKmsInNotEmptyTooltipOpen: state.totalKmsInNotEmptyTooltipOpen,
					setTotalKmsInNotEmptyTooltipOpen: state.setTotalKmsInNotEmptyTooltipOpen,
					totalKmsEmptyTooltipOpen: state.totalKmsEmptyTooltipOpen,
					setTotalKmsEmptyTooltipOpen: state.setTotalKmsEmptyTooltipOpen,
					totalKmsTooltipOpen: state.totalKmsTooltipOpen,
					setTotalKmsTooltipOpen: state.setTotalKmsTooltipOpen,
				}}
			/>
			<ServicePassengers
				isReserveService={calculations.isReserveService}
				passengers={state.passengers}
				onPassengersChange={handlers.handlePassengersCountChange}
				showPassengersTooltip={calculations.showPassengersTooltip}
				isSecondaryDriverService={isEditDisabled}
				setPassengersTooltipOpen={state.setPassengersTooltipOpen}
				passengersTooltipOpen={state.passengersTooltipOpen}
				serviceDetail={serviceDetail}
			/>
			<ServiceActions
				hasNotes={calculations.hasNotes}
				isReserveService={calculations.isReserveService}
				isClean={serviceDetail.serviceExecution?.isClean ?? false}
				onCleanChange={handlers.handleCleanChange}
				isSecondaryDriverService={isEditDisabled}
				instance={instance}
				onPrintService={e => onPrintService(e)}
			/>
		</Box>
	)
}
